import React, { useEffect, useRef, useState } from 'react';

/*
 * CardBackground
 * ------------------------------------------------------------------
 * Renders a fixed image as a terminal card background:
 *  - never repeats
 *  - keeps the image's aspect ratio (no distortion, whole image visible)
 *  - centers the image's *actual content* (not just its geometric middle),
 *    by measuring the tight bounding box of non-background pixels
 *  - fills the rest of the terminal so nothing is left black: a logo on a
 *    solid background is extended with that background colour, while busy /
 *    transparent images fall back to a blurred cover copy
 *  - stays centered for any card size / viewport aspect ratio (ResizeObserver)
 *
 * The image is drawn into a dedicated stage element and positioned manually,
 * which is the only way to guarantee the content center is truly centered:
 * CSS background-position / object-position align the image's P% point with
 * the container's P% point, which cannot move an off-center subject to
 * the middle.
 */

const metaCache = new Map();

// Measure intrinsic size, the center of the visible content, and the image's
// own background colour (when it has a near-uniform border).
function loadMeta(src) {
  if (metaCache.has(src)) return Promise.resolve(metaCache.get(src));

  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';

    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      let fx = 0.5;
      let fy = 0.5;
      let fill = null;
      let hasAlpha = false;

      try {
        const MAX = 256;
        const s = Math.min(1, MAX / Math.max(w, h));
        const cw = Math.max(1, Math.round(w * s));
        const ch = Math.max(1, Math.round(h * s));

        const canvas = document.createElement('canvas');
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, cw, ch);
        const data = ctx.getImageData(0, 0, cw, ch).data;

        // Does the image have transparency? Then alpha is the background.
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] < 250) { hasAlpha = true; break; }
        }

        // Otherwise sample the corners as the background color.
        let br = 0; let bg = 0; let bb = 0;
        [0, (cw - 1) * 4, (ch - 1) * cw * 4, ((ch - 1) * cw + (cw - 1)) * 4].forEach((i) => {
          br += data[i]; bg += data[i + 1]; bb += data[i + 2];
        });
        br /= 4; bg /= 4; bb /= 4;

        let minX = cw; let minY = ch; let maxX = -1; let maxY = -1;
        const tol = 42;
        for (let y = 0; y < ch; y += 1) {
          for (let x = 0; x < cw; x += 1) {
            const i = (y * cw + x) * 4;
            const content = hasAlpha
              ? data[i + 3] > 40
              : Math.abs(data[i] - br) + Math.abs(data[i + 1] - bg) + Math.abs(data[i + 2] - bb) > tol;
            if (content) {
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
            }
          }
        }

        if (maxX >= 0) {
          fx = ((minX + maxX) / 2) / cw;
          fy = ((minY + maxY) / 2) / ch;
        }

        // Sample the outer ring. If it's mostly opaque, treat its dominant /
        // average colour as the image background and extend it to fill the
        // terminal (covers logos on flat or lightly textured backgrounds).
        const rt = Math.max(1, Math.round(Math.min(cw, ch) * 0.04));
        const buckets = new Map();
        let ringCount = 0;
        let opaqueRing = 0;
        let sr = 0; let sg = 0; let sb = 0;
        for (let y = 0; y < ch; y += 1) {
          for (let x = 0; x < cw; x += 1) {
            if (!(x < rt || y < rt || x >= cw - rt || y >= ch - rt)) continue;
            const i = (y * cw + x) * 4;
            ringCount += 1;
            if (data[i + 3] < 40) continue;
            opaqueRing += 1;
            sr += data[i]; sg += data[i + 1]; sb += data[i + 2];
            const key = (data[i] >> 4) * 256 + (data[i + 1] >> 4) * 16 + (data[i + 2] >> 4);
            const b = buckets.get(key);
            if (b) { b.n += 1; b.r += data[i]; b.g += data[i + 1]; b.b += data[i + 2]; }
            else buckets.set(key, { n: 1, r: data[i], g: data[i + 1], b: data[i + 2] });
          }
        }

        let best = null;
        buckets.forEach((b) => { if (!best || b.n > best.n) best = b; });
        if (ringCount > 0 && opaqueRing / ringCount > 0.6) {
          if (best && best.n / ringCount > 0.45) {
            // Dominant, flat background colour.
            fill = `rgb(${Math.round(best.r / best.n)}, ${Math.round(best.g / best.n)}, ${Math.round(best.b / best.n)})`;
          } else {
            // Textured background — use its average colour.
            fill = `rgb(${Math.round(sr / opaqueRing)}, ${Math.round(sg / opaqueRing)}, ${Math.round(sb / opaqueRing)})`;
          }
        }
      } catch (e) {
        // Canvas unavailable / tainted — fall back to geometric center.
      }

      const meta = { w, h, fx, fy, fill, mode: hasAlpha ? 'contain' : 'cover' };
      metaCache.set(src, meta);
      resolve(meta);
    };

    img.onerror = () => {
      const meta = { w: 1, h: 1, fx: 0.5, fy: 0.5, fill: null, mode: 'contain' };
      metaCache.set(src, meta);
      resolve(meta);
    };

    img.src = src;
  });
}

function CardBackground({ src, focus }) {
  const stageRef = useRef(null);
  const [meta, setMeta] = useState(() => metaCache.get(src) || null);
  const [box, setBox] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!src) return undefined;
    const cached = metaCache.get(src);
    if (cached) {
      setMeta(cached);
    } else {
      setMeta(null);
      loadMeta(src).then((m) => { if (alive) setMeta(m); });
    }
    return () => { alive = false; };
  }, [src]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0].contentRect;
      setBox({ w: r.width, h: r.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  let imgStyle = null;
  if (meta && box && box.w > 0 && box.h > 0) {
    // Opaque images cover the terminal (fill it, cropping the excess); images
    // with transparency are contained so the whole logo stays visible.
    const mode = meta.mode || 'contain';
    const scale = mode === 'cover'
      ? Math.max(box.w / meta.w, box.h / meta.h)
      : Math.min(box.w / meta.w, box.h / meta.h);
    const dw = meta.w * scale;
    const dh = meta.h * scale;
    const fx = focus && typeof focus.x === 'number' ? focus.x : meta.fx;
    const fy = focus && typeof focus.y === 'number' ? focus.y : meta.fy;

    // Position so the chosen focal point lands on the stage center. The fill
    // layer covers any gap this may leave, so no clamping is needed.
    const tx = box.w / 2 - fx * dw;
    const ty = box.h / 2 - fy * dh;

    imgStyle = {
      width: `${dw}px`,
      height: `${dh}px`,
      transform: `translate(${tx}px, ${ty}px)`,
    };
  }

  const hasSolidFill = !!(meta && meta.fill);

  return (
    <div className="agent-window__bg" ref={stageRef} aria-hidden="true">
      {src && hasSolidFill && (
        <div
          className="agent-window__bg-fill agent-window__bg-fill--solid"
          style={{ backgroundColor: meta.fill }}
        />
      )}
      {src && !hasSolidFill && (
        <div
          className="agent-window__bg-fill"
          style={{ backgroundImage: `url("${src}")` }}
        />
      )}
      {src && imgStyle && <img src={src} alt="" draggable="false" style={imgStyle} />}
    </div>
  );
}

export default CardBackground;
